import LinkOffIcon from "@mui/icons-material/LinkOff";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import {LoadingButton} from "@mui/lab";
import {Alert, Box, Button, FormControl, InputLabel, ListItemText, MenuItem, Select, Skeleton, Stack, TextField} from "@mui/material";
import {formatDateHM} from "crqlar-react-common/dist/utils/date.util";
import React from "react";
import {useTranslation} from "react-i18next";
import {FOLIO_ALERT_SX, FOLIO_NOTE_SX} from "../../system/billing/folio/editor/folioStyles";
import {DigitalSigningStatusView} from "../digitalSigning.model";
import LanguageOption from "../LanguageOption";
import {DIGITAL_SIGNING_TEMPLATES_PATH} from "../settings/digitalSigningTemplate.model";
import DigitalSigningStatusLine from "./DigitalSigningStatusLine";
import {DIGITAL_SIGNING_BUTTON_SX} from "./digitalSigningFlow.util";
import {DigitalSigningFlow} from "./useDigitalSigning";

interface DraftProps {
    flow: DigitalSigningFlow;
}

export const ROOM_MAX_LENGTH = 20;
const TEMPLATE_LABEL_ID = 'digital-signing-template-label';
const NOTICE_PLACEHOLDER_HEIGHT = 120;

const ManageTemplatesLink = () => {
    const {t} = useTranslation();

    return (
        <Box>
            <Button
                size={'small'}
                color={'inherit'}
                component={'a'}
                href={DIGITAL_SIGNING_TEMPLATES_PATH}
                target={'_blank'}
                rel={'noopener noreferrer'}
                endIcon={<OpenInNewIcon sx={{fontSize: 14}}/>}
                sx={{color: 'text.secondary', textTransform: 'none', px: 0.5}}
            >
                {t('Manage templates')}
            </Button>
        </Box>
    );
};

/**
 * The page asks for a tip, but nobody would receive it: the recipient has no tip service, and the sector no
 * default one - so no link. Fixed in the template's Who gets the tip, in a new tab.
 */
export const TipWithoutServiceNotice = ({templateId}: {templateId?: string}) => {
    const {t} = useTranslation();

    return (
        <Alert severity={'error'} sx={FOLIO_ALERT_SX}>
            {/* The button under the text: beside it, the dialog leaves the text no room */}
            <Stack gap={1} alignItems={'flex-start'}>
                <Box>{t('Tipping is on, but nobody receives the tip: no tip service is connected.')}</Box>
                {templateId && (
                    <Button
                        color={'inherit'}
                        size={'small'}
                        variant={'outlined'}
                        component={'a'}
                        href={`${DIGITAL_SIGNING_TEMPLATES_PATH}/${templateId}?tab=tip`}
                        target={'_blank'}
                        rel={'noopener noreferrer'}
                        endIcon={<OpenInNewIcon sx={{fontSize: 14}}/>}
                        sx={{textTransform: 'none'}}
                    >
                        {t('Edit template')}
                    </Button>
                )}
            </Stack>
        </Alert>
    );
};

interface NoTemplateProps {
    loading: boolean;
    onCreate?: () => void;
    creating: boolean;
    error?: string;
}

const NoTemplateNotice = ({loading, onCreate, creating, error}: NoTemplateProps) => {
    const {t} = useTranslation();

    if (loading) return <Skeleton variant={'rounded'} height={NOTICE_PLACEHOLDER_HEIGHT}/>;
    return (
        <>
            <Alert severity={'info'} sx={FOLIO_ALERT_SX}>
                {onCreate ? (
                    <Stack gap={1} alignItems={'flex-start'}>
                        <Box>
                            {t('No template applies here yet. Create one from the standard template - it is used straight away and can be edited under Settings > Digital signing.')}
                        </Box>
                        <LoadingButton size={'small'} variant={'contained'} loading={creating} onClick={onCreate}>
                            {t('Create template')}
                        </LoadingButton>
                    </Stack>
                ) : t('No template applies here yet.')}
            </Alert>
            {error && <Alert severity={'error'} sx={FOLIO_ALERT_SX}>{error}</Alert>}
            <ManageTemplatesLink/>
        </>
    );
};

export const DigitalSigningDraft = ({flow}: DraftProps) => {
    const draftState = flow.state.kind === 'DRAFT' ? flow.state : undefined;
    const previous = draftState?.previous;
    const blockedBy = draftState?.blockedBy;
    const {
        busy,
        create,
        templates,
        templateId,
        setTemplateId,
        noTemplate,
        createTemplate,
        creatingTemplate,
        createTemplateError,
        persons,
        profileId,
        setProfileId,
        staff,
        tipStaffResourceId,
        setTipStaffResourceId,
        tipWithoutService,
        room,
        setRoom,
        language,
        languages,
        setLanguage,
        prefilling,
    } = flow;
    const {t} = useTranslation();
    const options = language && !languages.includes(language) ? [language, ...languages] : languages;

    return (
        <Stack gap={1.5}>
            <Box sx={FOLIO_NOTE_SX}>
                {previous === 'EXPIRED' ? t('The last link expired.')
                    : previous === 'VOIDED' ? t('The last link was voided.')
                        : t('The guest checks the services, chooses a tip and signs on the tablet.')}
            </Box>

            {blockedBy ? (
                <Box sx={{...FOLIO_NOTE_SX, color: 'text.primary'}}>
                    {blockedBy === 'NO_LINES' ? t('Add a line to the folio first.') : t('Every line needs a price first.')}
                </Box>
            ) : noTemplate ? (
                <NoTemplateNotice
                    loading={prefilling}
                    onCreate={createTemplate}
                    creating={creatingTemplate}
                    error={createTemplateError}
                />
            ) : (
                <>
                    {(prefilling || templates.length > 0) && (
                        <FormControl fullWidth size={'small'}>
                            <InputLabel id={TEMPLATE_LABEL_ID}>{t('Template')}</InputLabel>
                            <Select
                                labelId={TEMPLATE_LABEL_ID}
                                label={t('Template')}
                                value={templateId}
                                onChange={event => setTemplateId(event.target.value)}
                                disabled={busy || prefilling}
                                renderValue={value => templates.find(template => template.id === value)?.name}
                            >
                                {templates.map(template => (
                                    <MenuItem key={template.id} value={template.id}>
                                        <ListItemText primary={template.name}/>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                    {persons.length > 1 && (
                        <TextField
                            select
                            size={'small'}
                            fullWidth
                            label={t('Guest')}
                            value={profileId}
                            onChange={event => setProfileId(event.target.value)}
                            disabled={busy}
                            InputLabelProps={{shrink: true}}
                        >
                            {persons.map(person => (
                                <MenuItem key={person.profileId} value={person.profileId}>{person.name || t('Unknown')}</MenuItem>
                            ))}
                        </TextField>
                    )}
                    {staff.length > 1 && (
                        <TextField
                            select
                            size={'small'}
                            fullWidth
                            label={t('Tip for')}
                            value={tipStaffResourceId}
                            onChange={event => setTipStaffResourceId(event.target.value)}
                            disabled={busy}
                            InputLabelProps={{shrink: true}}
                        >
                            {staff.map(member => (
                                <MenuItem key={member.resourceId} value={member.resourceId}>{member.name || t('Unknown')}</MenuItem>
                            ))}
                        </TextField>
                    )}
                    {tipWithoutService && <TipWithoutServiceNotice templateId={templateId}/>}
                    <Stack direction={'row'} gap={1}>
                        <TextField
                            size={'small'}
                            label={t('Room')}
                            placeholder={t('optional')}
                            value={room}
                            onChange={event => setRoom(event.target.value)}
                            disabled={busy}
                            InputLabelProps={{shrink: true}}
                            inputProps={{maxLength: ROOM_MAX_LENGTH, autoComplete: 'off'}}
                            sx={{flex: 1}}
                        />
                        {(prefilling || options.length > 0) && (
                            <TextField
                                select
                                size={'small'}
                                label={t('Language')}
                                value={language}
                                onChange={event => setLanguage(event.target.value)}
                                disabled={busy || prefilling}
                                InputLabelProps={{shrink: true}}
                                SelectProps={{renderValue: value => <LanguageOption code={String(value)}/>}}
                                sx={{flex: 1}}
                            >
                                {options.map(code => <MenuItem key={code} value={code}><LanguageOption code={code}/></MenuItem>)}
                            </TextField>
                        )}
                    </Stack>
                    <ManageTemplatesLink/>
                    {/* Nobody would receive the tip: fixed in the template, or with another recipient */}
                    <LoadingButton
                        variant={'contained'}
                        fullWidth
                        startIcon={<QrCode2Icon/>}
                        loading={busy}
                        disabled={tipWithoutService}
                        onClick={create}
                        sx={DIGITAL_SIGNING_BUTTON_SX}
                    >
                        {previous ? t('Create a new link') : t('Create link')}
                    </LoadingButton>
                </>
            )}
        </Stack>
    );
};

interface StaleProps {
    signing: DigitalSigningStatusView;
    busy: boolean;
    onCreate: () => void;
}

export const DigitalSigningStale = ({signing, busy, onCreate}: StaleProps) => {
    const {t} = useTranslation();

    return (
        <Stack gap={1.5}>
            <DigitalSigningStatusLine signing={signing}/>
            <Box sx={FOLIO_NOTE_SX}>{t('The folio changed after the guest signed. Please create a new link.')}</Box>
            <LoadingButton
                variant={'contained'}
                fullWidth
                startIcon={<QrCode2Icon/>}
                loading={busy}
                onClick={onCreate}
                sx={DIGITAL_SIGNING_BUTTON_SX}
            >
                {t('New link')}
            </LoadingButton>
        </Stack>
    );
};

interface ReissueProps {
    signing: DigitalSigningStatusView;
    busy: boolean;
    onCreate: () => void;
    onVoid: () => void;
}

export const DigitalSigningReissue = ({signing, busy, onCreate, onVoid}: ReissueProps) => {
    const {t} = useTranslation();

    return (
        <Stack gap={1.5}>
            <DigitalSigningStatusLine signing={signing}/>

            <Box>
                <Box sx={{fontSize: 13, lineHeight: '18px'}}>
                    {t('Link issued at {{time}}', {time: formatDateHM(signing.issuedAt)})}
                </Box>
                <Box sx={FOLIO_NOTE_SX}>{t('The QR code can only be shown right after the link is created.')}</Box>
            </Box>

            <Stack gap={1}>
                <LoadingButton
                    variant={'contained'}
                    fullWidth
                    startIcon={<QrCode2Icon/>}
                    loading={busy}
                    onClick={onCreate}
                    sx={DIGITAL_SIGNING_BUTTON_SX}
                >
                    {t('Show a new QR code')}
                </LoadingButton>
                <Button
                    color={'error'}
                    fullWidth
                    startIcon={<LinkOffIcon/>}
                    disabled={busy}
                    onClick={onVoid}
                    sx={DIGITAL_SIGNING_BUTTON_SX}
                >
                    {t('Void link')}
                </Button>
            </Stack>
        </Stack>
    );
};
